import { DecimalPipe } from '@angular/common';
import { AfterViewInit, Component, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Data } from '@angular/router';
import { VulcanIconComponent } from '@eagle-i/vulcan-component-library';
import {debounceTime, forkJoin, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import { AppService } from '../../../../services/app.service';
import { ReportsService } from '../../../../services/reports.service';
import { Coverage } from '../../../../shared/classes/coverage';
import { FileDownload } from '../../../../shared/classes/fileDownload';
import { Report } from '../../classes/report';
import { ReportHeaderComponent } from '../report-header/report-header.component';

@Component({
  selector: 'tgrid-coverage-report',
  imports: [
    MatButtonToggleModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCardModule,
    MatPaginatorModule,
    MatMenuModule,
    MatSortModule,
    MatSelectModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    DecimalPipe,
    VulcanIconComponent,
    MatSort,
    MatPaginator,
    ReportHeaderComponent,

  ],
  templateUrl: './coverage-report.component.html',
  standalone: true,
  styleUrl: './coverage-report.component.scss',
})
export class CoverageReportComponent implements AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private appService = inject(AppService);
  public reportService = inject(ReportsService);
  public activatedRoute = inject(ActivatedRoute);


  public dataSource = new MatTableDataSource<Coverage>();
  public columns: string[] = ['county', 'state', 'year', 'coveredCustomers', 'totalCustomers', 'percentage'];

  protected report: Signal<Report> = toSignal(this.activatedRoute.data
    .pipe(takeUntilDestroyed(), map((data: Data) => {
      return data['report'];
    }),
    ));


  protected countyNameControl: FormControl<string | null> = new FormControl<string | null>(null);

  protected isLoading = signal(false);
  protected stateMetric: WritableSignal<Coverage | undefined> = signal(undefined);

  constructor() {
    //TODO: Move this to an RX resource
    const stateList = toObservable(this.appService.selectedStates);
    stateList.pipe(
      debounceTime(500),
      takeUntilDestroyed(),
      tap((s) => {
        this.isLoading.set(true);
      }),
      switchMap((stateList) =>
        forkJoin([
          this.reportService.getStateCoverageData(stateList),
          this.reportService.getCountyCoverageData(2024, stateList),
        ]).pipe(
          catchError((err) => {
            console.error('Failed to load coverage data:', err);
            this.isLoading.set(false);
            return of([[], []]); // emit empty fallback to keep stream alive
          }),
        ),
      ),
    ).subscribe({
      next: ([stateCoverage, countyCoverage]) => {
        this.stateMetric.set(stateCoverage[0]);
        this.dataSource.data = countyCoverage;
        this.isLoading.set(false);
      },
    });


    this.countyNameControl.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(),
      )
      .subscribe(value => {
        this.dataSource.filter = value ? value.toLowerCase() : '';
      });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.filterPredicate = this.filterLogic.bind(this);
    this.dataSource.sortingDataAccessor = this.sortAccessor.bind(this);
    this.dataSource.sort = this.sort;
    this.sort.active = 'county';


  }

  private sortAccessor(row: Coverage, headerName: string) {
    switch (headerName) {
      case 'county':
        return row.county.toLowerCase();
      case 'state':
        return row.state.toLowerCase();
      case 'year':
        return row.year;
      case 'coveredCustomers':
        return row.coveredCustomers;
      case 'totalCustomers':
        return row.totalCustomers;
      default:
        return -1;
    }
  }

  public filterLogic(currentRow: Coverage, searchText: string): boolean {
    return currentRow.county.toLowerCase().includes(searchText.toLowerCase().trim());
  }


  public exportAsCsv(): void {
    const stateHeaders = ['Total Customers', 'Covered Customers', 'Percentage'];
    const countyHeaders = ['County', 'State', 'Year', 'Covered Customers', 'Total Customers', 'Percentage'];
    let data = '';
    data += stateHeaders.join(',') + '\n';
    data += [
      this.stateMetric()?.totalCustomers,
      this.stateMetric()?.coveredCustomers,
      this.stateMetric()?.percentage,
    ].join(',') + '\n';

    data += '\n\n';

    data += countyHeaders.join(',') + '\n';
    this.dataSource._orderData(this.dataSource.filteredData).forEach(county => {
      data += [
        county.county,
        county.year,
        county.coveredCustomers,
        county.totalCustomers,
        county.percentage,
      ].join() + '\n';
    });

    const states = this.appService.selectedStates();
    let stateName = '';
    if (states.length === 1) {
      stateName = states[0].name;
    } else if (states.length > 1) {
      stateName = states.map(s => s.abbreviation).join(',');
    }


    FileDownload.downloadCSV(`${stateName}_2024_county_coverage`, data, this.report().attributionUrl);
  }
}
